import { memo, useEffect } from 'react'
import { SRGBColorSpace, EquirectangularReflectionMapping, Group, TextureLoader } from 'three'
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js'
import { useThree, useLoader } from '@react-three/fiber'
import { Environment } from '@react-three/drei'
import { useSceneProcessing } from './hooks/useSceneProcessing'
import { useKomorebiProjector } from './hooks/useKomorebiProjector'

const ENV_INTENSITY = 0.02
const ENV_ROTATION_DEG = 144
const BACKGROUND_INTENSITY = 1
const ENV_ROTATION: [number, number, number] = [0, (ENV_ROTATION_DEG * Math.PI) / 180, 0]

interface WorldProps {
  scene: Group
  onReady: () => void
}

function World({ scene, onReady }: WorldProps) {
  const rootScene = useThree((s) => s.scene)

  const envMap = useLoader(RGBELoader, '/rooitou_park_1k.hdr')
  envMap.mapping = EquirectangularReflectionMapping

  const backgroundMap = useLoader(TextureLoader, '/env.webp')
  backgroundMap.mapping = EquirectangularReflectionMapping
  backgroundMap.colorSpace = SRGBColorSpace

  // <Environment> reapplies background rotation/intensity to the scene on every
  // render (drei runs setEnvProps in a deps-less layout effect and fills in
  // defaults for any prop we omit), so it must own those — otherwise it resets
  // backgroundRotation to [0,0,0] right after we set it. We only own the
  // background *texture* here, since <Environment background={false}> won't set it.
  useEffect(() => {
    rootScene.background = backgroundMap
    return () => { rootScene.background = null }
  }, [rootScene, backgroundMap])

  // Komorebi projection patches its shader into the scene materials; hand the
  // patch to useSceneProcessing so it's baked into the single compile.
  const { patchMaterial, warmUp } = useKomorebiProjector(scene)
  const sceneReady = useSceneProcessing(scene, envMap, onReady, patchMaterial, warmUp)

  return (
    <>
      <Environment
        map={envMap}
        environmentIntensity={ENV_INTENSITY}
        environmentRotation={ENV_ROTATION}
        backgroundRotation={ENV_ROTATION}
        backgroundIntensity={BACKGROUND_INTENSITY}
      />
      <primitive object={scene} visible={sceneReady} />
    </>
  )
}

export default memo(World)
